import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import MainLayout from "../../components/Layout/MainLayout";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import IconPicker from "../../components/common/IconPicker";
import { api } from "../../services/api";
import {
  getGroupedScreenOptions,
  getScreenLabel,
} from "../../constants/appScreens";
import {
  ArrowBack,
  Save,
  ArrowForward,
  Info,
  Send,
  Settings,
  Preview,
} from "@mui/icons-material";
import * as Icons from "@mui/icons-material";
import {
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
  Box,
} from "@mui/material";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  flex: 1;
  ${(props) =>
    props.theme?.isDark &&
    `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const FormCard = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
`;

const StyledStepper = styled(Stepper)`
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  width: 100%;

  .MuiStep-root {
    padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm};
    border-radius: ${(props) =>
      getTheme(props.theme?.isDark).borderRadius.md};
    transition: all 0.3s ease;

    &.Mui-active {
      background-color: ${(props) =>
        getTheme(props.theme?.isDark).colors.primary}15;
      ${(props) =>
        props.theme?.isDark &&
        `
        box-shadow: 0 0 20px ${
          getTheme(true).colors.primary
        }30;
      `}
    }
  }

  .MuiStepLabel-root {
    .MuiStepLabel-label {
      color: ${(props) =>
        getTheme(props.theme?.isDark).colors.text};
      font-weight: 500;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    &.Mui-active .MuiStepLabel-label {
      color: ${(props) =>
        getTheme(props.theme?.isDark).colors.primary};
      font-weight: 700;
      font-size: 15px;
      ${(props) =>
        props.theme?.isDark &&
        `
        text-shadow: 0 0 10px ${
          getTheme(true).colors.primary
        }50;
      `}
    }

    &.Mui-completed .MuiStepLabel-label {
      color: ${(props) =>
        getTheme(props.theme?.isDark).colors.textSecondary};
    }
  }

  .MuiStepIcon-root {
    color: ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
    font-size: 1.75rem;
    transition: all 0.3s ease;

    &.Mui-active {
      color: ${(props) =>
        getTheme(props.theme?.isDark).colors.primary};
      transform: scale(1.2);
      ${(props) =>
        props.theme?.isDark &&
        `
        filter: drop-shadow(0 0 10px ${
          getTheme(true).colors.primary
        });
      `}
    }

    &.Mui-completed {
      color: ${(props) =>
        getTheme(props.theme?.isDark).colors.primary};
    }
  }

  .MuiStepConnector-root {
    top: 20px;

    .MuiStepConnector-line {
      border-color: ${(props) =>
        getTheme(props.theme?.isDark).colors.border};
      border-width: 2px;
      transition: all 0.3s ease;
    }

    &.Mui-active {
      .MuiStepConnector-line {
        border-color: ${(props) =>
          getTheme(props.theme?.isDark).colors.primary};
        border-width: 3px;
      }
    }

    &.Mui-completed {
      .MuiStepConnector-line {
        border-color: ${(props) =>
          getTheme(props.theme?.isDark).colors.primary};
      }
    }
  }
`;

const StepContentContainer = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const Required = styled.span`
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.error ||
    "#f44336"};
`;

const Input = styled.input`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${(props) =>
    getTheme(props.theme?.isDark).fonts.primary};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
    ${(props) =>
      props.theme?.isDark &&
      `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const TextArea = styled.textarea`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${(props) =>
    getTheme(props.theme?.isDark).fonts.primary};
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
    ${(props) =>
      props.theme?.isDark &&
      `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const Select = styled.select`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${(props) =>
    getTheme(props.theme?.isDark).fonts.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
    ${(props) =>
      props.theme?.isDark &&
      `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  cursor: pointer;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;

  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const ImageUpload = styled.div`
  border: 2px dashed
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
  }

  input[type="file"] {
    display: none;
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const FunnelSelect = styled(Select)`
  width: 100%;
`;

const TriggerCard = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const TriggerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const TriggerTitle = styled.div`
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const ActionButton = styled(Button)`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.xs}
    ${(props) => getTheme(props.theme?.isDark).spacing.sm};
  min-width: auto;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
  padding-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  border-top: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const ErrorMessage = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.error ||
    "#f44336"}20;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.error ||
    "#f44336"};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xxl};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const VariablesInfo = styled.div`
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.hover};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const VariablesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const VariableTag = styled.span`
  padding: 4px 8px;
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary}20;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.primary}40;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  font-family: monospace;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary}30;
    transform: translateY(-1px);
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.hover};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  cursor: pointer;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-weight: 600;
  font-size: 14px;
  flex: 1;
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 50px;
  height: 26px;
  background-color: ${(props) =>
    props.$checked
      ? getTheme(props.theme?.isDark).colors.primary
      : getTheme(props.theme?.isDark).colors.border};
  border-radius: 13px;
  transition: all 0.3s ease;
  cursor: pointer;

  &::after {
    content: "";
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: white;
    top: 3px;
    left: ${(props) => (props.$checked ? "27px" : "3px")};
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const PreviewCard = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const PreviewTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const PreviewBody = styled.div`
  font-size: 14px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  line-height: 1.6;
`;

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};

  .MuiTab-root {
    color: ${(props) =>
      getTheme(props.theme?.isDark).colors.textSecondary};
    text-transform: none;
    font-weight: 500;
    min-width: 80px;

    &.Mui-selected {
      color: ${(props) =>
        getTheme(props.theme?.isDark).colors.primary};
      font-weight: 600;
    }
  }

  .MuiTabs-indicator {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }
`;

const TabPanel = styled(Box)`
  padding-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const NotificationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = { isDark };
  const isEditMode = !!id;

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [funnels, setFunnels] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [mediaType, setMediaType] = useState("image"); // 'image' or 'icon'
  const [promptTemplates, setPromptTemplates] = useState(
    []
  );
  const [activeTitleTab, setActiveTitleTab] = useState(0);
  const [activeBodyTab, setActiveBodyTab] = useState(0);
  const [activeButtonLabelTab, setActiveButtonLabelTab] =
    useState(0);

  const [formData, setFormData] = useState({
    key: "",
    title: { az: "", en: "", ru: "" },
    body: { az: "", en: "", ru: "" },
    deepLink: "",
    actionType: "MODAL",
    variant: "STANDARD",
    channelTypes: [],
    sound: "default",
    priority: "high",
    saveToUserHistory: true,
    titleTextColor: "#000000",
    bodyTextColor: "#000000",
    backgroundColor: "#FFFFFF",
    aiMode: false,
    aiPrompt: "",
    aiModel: "gpt-4",
    promptTemplateKey: "",
    enabled: true,
    deepLinkButton: {
      label: { az: "", en: "", ru: "" },
      type: "PRIMARY",
      icon: "",
    },
    icon: "",
    triggers: [],
  });

  const steps = [
    { label: "Əsas Məlumatlar", icon: <Info /> },
    { label: "Çatdırılma Kanalları", icon: <Send /> },
    {
      label: "Trigger Konfiqurasiyası",
      icon: <Settings />,
    },
    { label: "Baxış", icon: <Preview /> },
  ];

  useEffect(() => {
    if (isEditMode) {
      fetchNotification();
    }
    fetchFunnels();
    fetchPromptTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  // Helper function to normalize title/body from legacy string or object format
  const normalizeLocalizedField = (field) => {
    if (!field) {
      return { az: "", en: "", ru: "" };
    }
    // If it's already an object with locale keys, return it
    if (
      typeof field === "object" &&
      field !== null &&
      !Array.isArray(field)
    ) {
      return {
        az: field.az || "",
        en: field.en || "",
        ru: field.ru || "",
      };
    }
    // If it's a string (legacy format), convert to object with az as default
    if (typeof field === "string") {
      return {
        az: field,
        en: "",
        ru: "",
      };
    }
    return { az: "", en: "", ru: "" };
  };

  // Helper function to normalize deepLink - remove fit:// prefix for display in dropdown
  const normalizeDeepLinkForDisplay = (deepLink) => {
    if (!deepLink) return "";
    // Remove fit:// prefix if present
    return deepLink.replace(/^fit:\/\//, "");
  };

  // Helper function to normalize deepLink - add fit:// prefix for saving
  const normalizeDeepLinkForSave = (deepLink) => {
    if (!deepLink) return "";
    // Add fit:// prefix if not present
    if (!deepLink.startsWith("fit://")) {
      return `fit://${deepLink}`;
    }
    return deepLink;
  };

  const fetchNotification = async () => {
    try {
      setLoading(true);
      const notification = await api.getNotification(id);
      setFormData({
        key: notification.key || "",
        title: normalizeLocalizedField(notification.title),
        body: normalizeLocalizedField(notification.body),
        deepLink: normalizeDeepLinkForDisplay(
          notification.deepLink || ""
        ),
        actionType: notification.actionType || "MODAL",
        variant: notification.variant || "STANDARD",
        channelTypes: notification.channelTypes || [],
        sound: notification.sound || "default",
        priority: notification.priority || "high",
        saveToUserHistory:
          notification.saveToUserHistory !== undefined
            ? notification.saveToUserHistory
            : true,
        titleTextColor:
          notification.titleTextColor || "#000000",
        bodyTextColor:
          notification.bodyTextColor || "#000000",
        backgroundColor:
          notification.backgroundColor || "#FFFFFF",
        aiMode: notification.aiMode || false,
        aiPrompt: notification.aiPrompt || "",
        aiModel: notification.aiModel || "gpt-4",
        promptTemplateKey:
          notification.promptTemplateKey || "",
        enabled:
          notification.enabled !== undefined
            ? notification.enabled
            : true,
        deepLinkButton: {
          label: normalizeLocalizedField(
            notification.deepLinkButton?.label
          ),
          type:
            notification.deepLinkButton?.type || "PRIMARY",
          icon: notification.deepLinkButton?.icon || "",
        },
        icon: notification.icon || "",
        triggers: notification.triggers || [],
      });
      if (notification.image) {
        setImagePreview(
          notification.imagePath || notification.image
        );
        setMediaType("image");
      } else if (notification.icon) {
        setMediaType("icon");
      }
    } catch (err) {
      console.error("Error fetching notification:", err);
      setError(
        "Bildiriş məlumatları yüklənərkən xəta baş verdi"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchFunnels = async () => {
    try {
      const response = await api.getCustomerFunnels({
        limit: 100,
        isActive: true,
      });
      setFunnels(response.results || []);
    } catch (err) {
      console.error("Error fetching funnels:", err);
    }
  };

  const fetchPromptTemplates = async () => {
    try {
      const response = await api.getPromptTemplates({
        limit: 1000,
        filter: { isActive: true },
      });
      // Group templates by key and get unique keys
      const templates = response.results || [];
      setPromptTemplates(templates);
    } catch (err) {
      console.error(
        "Error fetching prompt templates:",
        err
      );
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Auto-update colors based on variant
      if (field === "variant") {
        const variantColors = {
          PRIMARY: {
            backgroundColor: "#E3F2FD",
            titleTextColor: "#0D47A1",
            bodyTextColor: "#1565C0",
          },
          STANDARD: {
            backgroundColor: "#FFFFFF",
            titleTextColor: "#000000",
            bodyTextColor: "#000000",
          },
          SUCCESS: {
            backgroundColor: "#E8F5E9",
            titleTextColor: "#2E7D32",
            bodyTextColor: "#1B5E20",
          },
          WARNING: {
            backgroundColor: "#FFF3E0",
            titleTextColor: "#E65100",
            bodyTextColor: "#BF360C",
          },
          DANGER: {
            backgroundColor: "#FFEBEE",
            titleTextColor: "#C62828",
            bodyTextColor: "#B71C1C",
          },
        };

        const colors =
          variantColors[value] || variantColors.STANDARD;
        newData.backgroundColor = colors.backgroundColor;
        newData.titleTextColor = colors.titleTextColor;
        newData.bodyTextColor = colors.bodyTextColor;
      }

      // Auto-fill aiPrompt from Prompt Template if aiMode is enabled and promptTemplateKey is set
      if (
        (field === "aiMode" && value === true) ||
        field === "promptTemplateKey"
      ) {
        const promptTemplateKey =
          field === "promptTemplateKey"
            ? value
            : newData.promptTemplateKey;
        const aiMode =
          field === "aiMode" ? value : newData.aiMode;

        if (aiMode && promptTemplateKey) {
          // Find template by key and locale (prioritize 'az', then 'en', then 'ru')
          const locales = ["az", "en", "ru"];
          let template = null;

          for (const locale of locales) {
            template = promptTemplates.find(
              (t) =>
                t.key === promptTemplateKey &&
                t.locale === locale &&
                t.isActive
            );
            if (template) break;
          }

          if (template && template.content) {
            newData.aiPrompt = template.content;
          }
        }
      }

      return newData;
    });
  };

  // Helper function to handle localized field changes (title or body with locale)
  const handleLocalizedFieldChange = (
    field,
    locale,
    value
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [locale]: value,
      },
    }));
  };

  // Helper function to insert variable into field
  // For localized fields (title, body): insertVariable(field, locale, variable)
  // For non-localized fields (aiPrompt): insertVariable(field, variable)
  const insertVariable = (
    field,
    localeOrVariable,
    variable
  ) => {
    // If variable is undefined, it means localeOrVariable is actually the variable (for aiPrompt)
    if (variable === undefined) {
      const currentValue = formData[field] || "";
      const variableText = `\${${localeOrVariable}}`;
      handleInputChange(field, currentValue + variableText);
    } else {
      // For localized fields (title, body)
      const locale = localeOrVariable;
      const currentValue = formData[field]?.[locale] || "";
      const variableText = `\${${variable}}`;
      handleLocalizedFieldChange(
        field,
        locale,
        currentValue + variableText
      );
    }
  };

  const handleChannelTypeChange = (channelType) => {
    setFormData((prev) => ({
      ...prev,
      channelTypes: [channelType], // Only one channel type can be selected
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addTrigger = () => {
    setFormData((prev) => ({
      ...prev,
      triggers: [
        ...(prev.triggers || []),
        {
          type: "CUSTOMER_FUNNEL",
          funnelId: null,
          actionType: null,
          delay: 0,
          isRecurring: false,
          scheduleTime: null,
          conditions: {},
        },
      ],
    }));
  };

  const removeTrigger = (index) => {
    setFormData((prev) => ({
      ...prev,
      triggers: prev.triggers.filter((_, i) => i !== index),
    }));
  };

  const updateTrigger = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      triggers: prev.triggers.map((trigger, i) =>
        i === index
          ? { ...trigger, [field]: value }
          : trigger
      ),
    }));
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) =>
        Math.min(prev + 1, steps.length - 1)
      );
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        if (!formData.key || !formData.variant) {
          setError("Key və tip mütləqdir");
          return false;
        }
        if (formData.aiMode) {
          if (!formData.aiPrompt) {
            setError(
              "AI mode aktivdirsə, AI prompt mütləqdir"
            );
            return false;
          }
        } else {
          // Validate localized title and body - at least az (default) must be filled
          const titleAz = formData.title?.az?.trim();
          const bodyAz = formData.body?.az?.trim();
          if (!titleAz || !bodyAz) {
            setError(
              "AI mode passivdirsə, başlıq və məzmun (ən azı Azərbaycan dili) mütləqdir"
            );
            return false;
          }
        }
        break;
      case 1:
        if (
          !formData.channelTypes ||
          formData.channelTypes.length !== 1
        ) {
          setError(
            "Dəqiq bir çatdırılma kanalı seçilməlidir"
          );
          return false;
        }
        break;
      default:
        break;
    }
    setError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setError("");
    setSaving(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("key", formData.key);
      // Send title and body as JSON strings for localized objects
      formDataToSend.append(
        "title",
        JSON.stringify(formData.title)
      );
      formDataToSend.append(
        "body",
        JSON.stringify(formData.body)
      );
      formDataToSend.append(
        "deepLink",
        normalizeDeepLinkForSave(formData.deepLink || "")
      );
      formDataToSend.append(
        "actionType",
        formData.actionType || "MODAL"
      );
      formDataToSend.append("variant", formData.variant);
      formDataToSend.append("sound", formData.sound);
      formDataToSend.append("priority", formData.priority);
      formDataToSend.append(
        "saveToUserHistory",
        formData.saveToUserHistory
      );
      formDataToSend.append(
        "titleTextColor",
        formData.titleTextColor
      );
      formDataToSend.append(
        "bodyTextColor",
        formData.bodyTextColor
      );
      formDataToSend.append(
        "backgroundColor",
        formData.backgroundColor
      );
      formDataToSend.append("aiMode", formData.aiMode);
      formDataToSend.append(
        "aiPrompt",
        formData.aiPrompt || ""
      );
      formDataToSend.append(
        "aiModel",
        formData.aiModel || "gpt-4"
      );
      formDataToSend.append(
        "promptTemplateKey",
        formData.promptTemplateKey || ""
      );
      formDataToSend.append("enabled", formData.enabled);
      formDataToSend.append(
        "channelTypes",
        JSON.stringify(formData.channelTypes)
      );
      formDataToSend.append(
        "deepLinkButton",
        JSON.stringify(formData.deepLinkButton)
      );
      formDataToSend.append(
        "triggers",
        JSON.stringify(formData.triggers)
      );

      // Handle media type: either image or icon, but not both
      if (mediaType === "image" && imageFile) {
        formDataToSend.append("image", imageFile);
        // Clear icon if image is selected
        formDataToSend.append("icon", "");
      } else if (mediaType === "icon") {
        // Icon is a string (Material-UI icon name), not a file
        formDataToSend.append("icon", formData.icon || "");
        // Clear image if icon is selected
        if (imageFile) {
          setImageFile(null);
          setImagePreview(null);
        }
      }

      if (isEditMode) {
        await api.updateNotification(id, formDataToSend);
      } else {
        await api.createNotification(formDataToSend);
      }

      navigate("/admin/notifications");
    } catch (err) {
      console.error("Error saving notification:", err);
      setError(
        err.response?.data?.message ||
          "Bildiriş saxlanarkən xəta baş verdi"
      );
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <StepContentContainer theme={theme}>
            <FormGroup theme={theme}>
              <Label theme={theme}>
                Key <Required>*</Required>
              </Label>
              <Input
                type="text"
                value={formData.key}
                onChange={(e) =>
                  handleInputChange("key", e.target.value)
                }
                placeholder="məs: welcome_notification"
                required
                theme={theme}
                disabled={isEditMode}
              />
              <small
                style={{
                  color:
                    getTheme(isDark).colors.textSecondary,
                }}
              >
                Unikal identifikator (dəyişdirilə bilməz)
              </small>
            </FormGroup>

            <ToggleContainer theme={theme}>
              <ToggleLabel theme={theme}>
                AI Mode
                <small
                  style={{
                    color:
                      getTheme(isDark).colors.textSecondary,
                    fontWeight: "normal",
                  }}
                >
                  (AI ilə başlıq və məzmun avtomatik
                  yaradılır)
                </small>
              </ToggleLabel>
              <ToggleSwitch
                theme={theme}
                $checked={formData.aiMode}
                onClick={() =>
                  handleInputChange(
                    "aiMode",
                    !formData.aiMode
                  )
                }
              />
            </ToggleContainer>

            <ToggleContainer theme={theme}>
              <ToggleLabel theme={theme}>
                Bildiriş Aktivdir
                <small
                  style={{
                    color:
                      getTheme(isDark).colors.textSecondary,
                    fontWeight: "normal",
                  }}
                >
                  (Passiv olarsa, bildiriş jobları
                  tərəfindən işlənməyəcək)
                </small>
              </ToggleLabel>
              <ToggleSwitch
                theme={theme}
                $checked={formData.enabled}
                onClick={() =>
                  handleInputChange(
                    "enabled",
                    !formData.enabled
                  )
                }
              />
            </ToggleContainer>

            {formData.aiMode ? (
              <>
                <FormGroup theme={theme}>
                  <Label theme={theme}>
                    Prompt Template Key
                  </Label>
                  <Select
                    value={formData.promptTemplateKey}
                    onChange={(e) =>
                      handleInputChange(
                        "promptTemplateKey",
                        e.target.value
                      )
                    }
                    theme={theme}
                  >
                    <option value="">
                      Prompt Template seçin
                    </option>
                    {Array.from(
                      new Set(
                        promptTemplates.map((t) => t.key)
                      )
                    )
                      .filter((key) =>
                        promptTemplates.some(
                          (t) =>
                            t.key === key &&
                            t.locale === "az" &&
                            t.isActive
                        )
                      )
                      .map((key) => (
                        <option key={key} value={key}>
                          {key}
                        </option>
                      ))}
                  </Select>
                  <small
                    style={{
                      color:
                        getTheme(isDark).colors
                          .textSecondary,
                    }}
                  >
                    Əgər Prompt Template seçilsə, AI Prompt
                    avtomatik doldurulacaq
                  </small>
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>
                    AI Prompt <Required>*</Required>
                  </Label>
                  <TextArea
                    value={formData.aiPrompt}
                    onChange={(e) =>
                      handleInputChange(
                        "aiPrompt",
                        e.target.value
                      )
                    }
                    placeholder="Məs: {name} üçün günlük kalori hədəfi {calorieGoal} kcal-dır. {consumedCalories} kcal alınıb, {remainingCalories} kcal qalıb. Motivasiya verən bildiriş hazırla"
                    required
                    theme={theme}
                    rows={4}
                  />
                  <VariablesInfo theme={theme}>
                    <strong>Mövcud dəyişənlər:</strong>
                    <VariablesList theme={theme}>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable("aiPrompt", "name")
                        }
                      >
                        ${"name"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "weight"
                          )
                        }
                      >
                        ${"weight"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "height"
                          )
                        }
                      >
                        ${"height"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "weightTo"
                          )
                        }
                      >
                        ${"weightTo"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "gender"
                          )
                        }
                      >
                        ${"gender"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable("aiPrompt", "bmi")
                        }
                      >
                        ${"bmi"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "activityLevel"
                          )
                        }
                      >
                        ${"activityLevel"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "phone"
                          )
                        }
                      >
                        ${"phone"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "email"
                          )
                        }
                      >
                        ${"email"}
                      </VariableTag>
                    </VariablesList>
                    <div
                      style={{
                        marginTop:
                          getTheme(isDark).spacing.sm,
                        fontSize: "11px",
                        color:
                          getTheme(isDark).colors
                            .textSecondary,
                      }}
                    >
                      <strong>Gündəlik məlumatlar:</strong>
                    </div>
                    <VariablesList theme={theme}>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "calorieGoal"
                          )
                        }
                      >
                        ${"calorieGoal"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "consumedCalories"
                          )
                        }
                      >
                        ${"consumedCalories"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "remainingCalories"
                          )
                        }
                      >
                        ${"remainingCalories"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "waterGoal"
                          )
                        }
                      >
                        ${"waterGoal"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "consumedWater"
                          )
                        }
                      >
                        ${"consumedWater"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "remainingWater"
                          )
                        }
                      >
                        ${"remainingWater"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "activityGoal"
                          )
                        }
                      >
                        ${"activityGoal"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "consumedSteps"
                          )
                        }
                      >
                        ${"consumedSteps"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "remainingSteps"
                          )
                        }
                      >
                        ${"remainingSteps"}
                      </VariableTag>
                      <VariableTag
                        theme={theme}
                        onClick={() =>
                          insertVariable(
                            "aiPrompt",
                            "todayMealPlan"
                          )
                        }
                      >
                        ${"todayMealPlan"}
                      </VariableTag>
                    </VariablesList>
                  </VariablesInfo>
                  <small
                    style={{
                      color:
                        getTheme(isDark).colors
                          .textSecondary,
                      marginTop:
                        getTheme(isDark).spacing.xs,
                      display: "block",
                    }}
                  >
                    AI-ya veriləcək təlimat. Başlıq və
                    məzmun bu prompta əsasən yaradılacaq.
                    Dəyişənlər bildiriş göndəriləndə real
                    məlumatlarla əvəz olunacaq.
                  </small>
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>AI Model</Label>
                  <Select
                    value={formData.aiModel}
                    onChange={(e) =>
                      handleInputChange(
                        "aiModel",
                        e.target.value
                      )
                    }
                    theme={theme}
                  >
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-4-turbo">
                      GPT-4 Turbo
                    </option>
                    <option value="gpt-3.5-turbo">
                      GPT-3.5 Turbo
                    </option>
                  </Select>
                  <small
                    style={{
                      color:
                        getTheme(isDark).colors
                          .textSecondary,
                    }}
                  >
                    İstifadə ediləcək AI model
                  </small>
                </FormGroup>
              </>
            ) : (
              <>
                <FormGroup theme={theme}>
                  <Label theme={theme}>
                    Başlıq <Required>*</Required>
                  </Label>
                  <StyledTabs
                    value={activeTitleTab}
                    onChange={(e, newValue) =>
                      setActiveTitleTab(newValue)
                    }
                    theme={theme}
                  >
                    <Tab label="Azərbaycan (az)" />
                    <Tab label="English (en)" />
                    <Tab label="Русский (ru)" />
                  </StyledTabs>
                  {["az", "en", "ru"].map(
                    (locale, index) => (
                      <TabPanel
                        key={locale}
                        role="tabpanel"
                        hidden={activeTitleTab !== index}
                        theme={theme}
                      >
                        <Input
                          type="text"
                          value={
                            formData.title?.[locale] || ""
                          }
                          onChange={(e) =>
                            handleLocalizedFieldChange(
                              "title",
                              locale,
                              e.target.value
                            )
                          }
                          placeholder={`Bildiriş başlığı - ${
                            locale === "az"
                              ? "Azərbaycan"
                              : locale === "en"
                              ? "English"
                              : "Русский"
                          } (məs: Salam {name}!)`}
                          required={index === 0}
                          theme={theme}
                        />
                        <VariablesInfo theme={theme}>
                          <strong>
                            Mövcud dəyişənlər:
                          </strong>
                          <VariablesList theme={theme}>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "name"
                                )
                              }
                            >
                              ${"name"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "weight"
                                )
                              }
                            >
                              ${"weight"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "height"
                                )
                              }
                            >
                              ${"height"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "weightTo"
                                )
                              }
                            >
                              ${"weightTo"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "gender"
                                )
                              }
                            >
                              ${"gender"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "bmi"
                                )
                              }
                            >
                              ${"bmi"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "activityLevel"
                                )
                              }
                            >
                              ${"activityLevel"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "phone"
                                )
                              }
                            >
                              ${"phone"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "email"
                                )
                              }
                            >
                              ${"email"}
                            </VariableTag>
                          </VariablesList>
                          <div
                            style={{
                              marginTop:
                                getTheme(isDark).spacing.sm,
                              fontSize: "11px",
                              color:
                                getTheme(isDark).colors
                                  .textSecondary,
                            }}
                          >
                            <strong>
                              Gündəlik məlumatlar:
                            </strong>
                          </div>
                          <VariablesList theme={theme}>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "calorieGoal"
                                )
                              }
                            >
                              ${"calorieGoal"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "consumedCalories"
                                )
                              }
                            >
                              ${"consumedCalories"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "remainingCalories"
                                )
                              }
                            >
                              ${"remainingCalories"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "waterGoal"
                                )
                              }
                            >
                              ${"waterGoal"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "consumedWater"
                                )
                              }
                            >
                              ${"consumedWater"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "remainingWater"
                                )
                              }
                            >
                              ${"remainingWater"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "activityGoal"
                                )
                              }
                            >
                              ${"activityGoal"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "consumedSteps"
                                )
                              }
                            >
                              ${"consumedSteps"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "remainingSteps"
                                )
                              }
                            >
                              ${"remainingSteps"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "title",
                                  locale,
                                  "todayMealPlan"
                                )
                              }
                            >
                              ${"todayMealPlan"}
                            </VariableTag>
                          </VariablesList>
                        </VariablesInfo>
                      </TabPanel>
                    )
                  )}
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>
                    Məzmun <Required>*</Required>
                  </Label>
                  <StyledTabs
                    value={activeBodyTab}
                    onChange={(e, newValue) =>
                      setActiveBodyTab(newValue)
                    }
                    theme={theme}
                  >
                    <Tab label="Azərbaycan (az)" />
                    <Tab label="English (en)" />
                    <Tab label="Русский (ru)" />
                  </StyledTabs>
                  {["az", "en", "ru"].map(
                    (locale, index) => (
                      <TabPanel
                        key={locale}
                        role="tabpanel"
                        hidden={activeBodyTab !== index}
                        theme={theme}
                      >
                        <TextArea
                          value={
                            formData.body?.[locale] || ""
                          }
                          onChange={(e) =>
                            handleLocalizedFieldChange(
                              "body",
                              locale,
                              e.target.value
                            )
                          }
                          placeholder={`Bildiriş məzmunu - ${
                            locale === "az"
                              ? "Azərbaycan"
                              : locale === "en"
                              ? "English"
                              : "Русский"
                          } (məs: {name}, sizin çəkiniz {weight} kq-dır)`}
                          required={index === 0}
                          theme={theme}
                        />
                        <VariablesInfo theme={theme}>
                          <strong>
                            Mövcud dəyişənlər:
                          </strong>
                          <VariablesList theme={theme}>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "name"
                                )
                              }
                            >
                              ${"name"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "weight"
                                )
                              }
                            >
                              ${"weight"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "height"
                                )
                              }
                            >
                              ${"height"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "weightTo"
                                )
                              }
                            >
                              ${"weightTo"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "gender"
                                )
                              }
                            >
                              ${"gender"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "bmi"
                                )
                              }
                            >
                              ${"bmi"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "activityLevel"
                                )
                              }
                            >
                              ${"activityLevel"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "phone"
                                )
                              }
                            >
                              ${"phone"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "email"
                                )
                              }
                            >
                              ${"email"}
                            </VariableTag>
                          </VariablesList>
                          <div
                            style={{
                              marginTop:
                                getTheme(isDark).spacing.sm,
                              fontSize: "11px",
                              color:
                                getTheme(isDark).colors
                                  .textSecondary,
                            }}
                          >
                            <strong>
                              Gündəlik məlumatlar:
                            </strong>
                          </div>
                          <VariablesList theme={theme}>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "calorieGoal"
                                )
                              }
                            >
                              ${"calorieGoal"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "consumedCalories"
                                )
                              }
                            >
                              ${"consumedCalories"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "remainingCalories"
                                )
                              }
                            >
                              ${"remainingCalories"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "waterGoal"
                                )
                              }
                            >
                              ${"waterGoal"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "consumedWater"
                                )
                              }
                            >
                              ${"consumedWater"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "remainingWater"
                                )
                              }
                            >
                              ${"remainingWater"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "activityGoal"
                                )
                              }
                            >
                              ${"activityGoal"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "consumedSteps"
                                )
                              }
                            >
                              ${"consumedSteps"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "remainingSteps"
                                )
                              }
                            >
                              ${"remainingSteps"}
                            </VariableTag>
                            <VariableTag
                              theme={theme}
                              onClick={() =>
                                insertVariable(
                                  "body",
                                  locale,
                                  "todayMealPlan"
                                )
                              }
                            >
                              ${"todayMealPlan"}
                            </VariableTag>
                          </VariablesList>
                        </VariablesInfo>
                      </TabPanel>
                    )
                  )}
                </FormGroup>
              </>
            )}

            <FormGroup theme={theme}>
              <Label theme={theme}>Media Tipi</Label>
              <RadioGroup theme={theme}>
                <RadioLabel theme={theme}>
                  <input
                    type="radio"
                    name="mediaType"
                    value="image"
                    checked={mediaType === "image"}
                    onChange={(e) => {
                      setMediaType("image");
                      if (formData.icon) {
                        handleInputChange("icon", "");
                      }
                    }}
                  />
                  Şəkil
                </RadioLabel>
                <RadioLabel theme={theme}>
                  <input
                    type="radio"
                    name="mediaType"
                    value="icon"
                    checked={mediaType === "icon"}
                    onChange={(e) => {
                      setMediaType("icon");
                      if (imageFile || imagePreview) {
                        setImageFile(null);
                        setImagePreview(null);
                      }
                    }}
                  />
                  Icon
                </RadioLabel>
              </RadioGroup>
              <small
                style={{
                  color:
                    getTheme(isDark).colors.textSecondary,
                }}
              >
                Yalnız birini seçin: ya şəkil, ya icon
              </small>
            </FormGroup>

            {mediaType === "image" && (
              <FormGroup theme={theme}>
                <Label theme={theme}>Şəkil</Label>
                <ImageUpload
                  theme={theme}
                  onClick={() =>
                    document
                      .getElementById("image-upload")
                      .click()
                  }
                >
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {imagePreview ? (
                    <>
                      <ImagePreview
                        src={imagePreview}
                        alt="Preview"
                        theme={theme}
                      />
                      <Button
                        theme={theme}
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageFile(null);
                          setImagePreview(null);
                        }}
                        style={{ marginTop: "8px" }}
                      >
                        Şəkli Sil
                      </Button>
                    </>
                  ) : (
                    <div>
                      <p>Şəkil yükləmək üçün klikləyin</p>
                      <small>PNG, JPG və ya GIF</small>
                    </div>
                  )}
                </ImageUpload>
              </FormGroup>
            )}

            {mediaType === "icon" && (
              <FormGroup theme={theme}>
                <Label theme={theme}>Icon</Label>
                <IconPicker
                  value={formData.icon || ""}
                  onChange={(iconName) =>
                    handleInputChange("icon", iconName)
                  }
                  theme={theme}
                />
                <small
                  style={{
                    color:
                      getTheme(isDark).colors.textSecondary,
                    marginTop: getTheme(isDark).spacing.xs,
                    display: "block",
                  }}
                >
                  Material-UI icon seçin (dairəvi fonda
                  göstəriləcək)
                </small>
              </FormGroup>
            )}

            <FormGroup theme={theme}>
              <Label theme={theme}>
                Tip <Required>*</Required>
              </Label>
              <Select
                value={formData.variant}
                onChange={(e) =>
                  handleInputChange(
                    "variant",
                    e.target.value
                  )
                }
                theme={theme}
                required
              >
                <option value="PRIMARY">Primary</option>
                <option value="STANDARD">Standart</option>
                <option value="SUCCESS">Success</option>
                <option value="WARNING">Warning</option>
                <option value="DANGER">Danger</option>
              </Select>
              <small
                style={{
                  color:
                    getTheme(isDark).colors.textSecondary,
                }}
              >
                Bildirişin görünüşü və rəng sxemi
              </small>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Deep Link</Label>
              <Select
                value={formData.deepLink}
                onChange={(e) =>
                  handleInputChange(
                    "deepLink",
                    e.target.value
                  )
                }
                theme={theme}
              >
                <option value="">Deep Link seçin...</option>
                {getGroupedScreenOptions().map((group) => {
                  if (group.type === "group") {
                    return (
                      <optgroup
                        key={group.label}
                        label={group.label}
                      >
                        {group.options.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                    );
                  }
                  return (
                    <option
                      key={group.value}
                      value={group.value}
                    >
                      {group.label}
                    </option>
                  );
                })}
              </Select>
              <small
                style={{
                  color:
                    getTheme(isDark).colors.textSecondary,
                }}
              >
                Bildirişə kliklədikdə yönləndiriləcək app
                səhifəsi
              </small>
            </FormGroup>

            {formData.deepLink && (
              <>
                <FormGroup theme={theme}>
                  <Label theme={theme}>
                    Navigasiya Üsulu
                  </Label>
                  <RadioGroup theme={theme}>
                    <RadioLabel theme={theme}>
                      <input
                        type="radio"
                        name="actionType"
                        value="DIRECT_LINK"
                        checked={
                          formData.actionType ===
                          "DIRECT_LINK"
                        }
                        onChange={(e) =>
                          handleInputChange(
                            "actionType",
                            e.target.value
                          )
                        }
                      />
                      Birbaşa (Dərhal səhifəyə yönləndir)
                    </RadioLabel>
                    <RadioLabel theme={theme}>
                      <input
                        type="radio"
                        name="actionType"
                        value="MODAL"
                        checked={
                          formData.actionType === "MODAL"
                        }
                        onChange={(e) =>
                          handleInputChange(
                            "actionType",
                            e.target.value
                          )
                        }
                      />
                      Button ilə (Modal açılır, button ilə
                      yönləndir)
                    </RadioLabel>
                  </RadioGroup>
                  <small
                    style={{
                      color:
                        getTheme(isDark).colors
                          .textSecondary,
                    }}
                  >
                    {formData.actionType === "DIRECT_LINK"
                      ? "Bildirişə kliklədikdə birbaşa səhifəyə yönləndiriləcək"
                      : "Bildirişə kliklədikdə modal açılacaq və button ilə yönləndiriləcək"}
                  </small>
                </FormGroup>

                {formData.actionType === "MODAL" && (
                  <>
                    <FormGroup theme={theme}>
                      <Label theme={theme}>
                        Button Label
                      </Label>
                      <StyledTabs
                        value={activeButtonLabelTab}
                        onChange={(e, newValue) =>
                          setActiveButtonLabelTab(newValue)
                        }
                        theme={theme}
                      >
                        <Tab label="Azərbaycan (az)" />
                        <Tab label="English (en)" />
                        <Tab label="Русский (ru)" />
                      </StyledTabs>
                      {["az", "en", "ru"].map(
                        (locale, index) => (
                          <TabPanel
                            key={locale}
                            role="tabpanel"
                            hidden={
                              activeButtonLabelTab !== index
                            }
                            theme={theme}
                          >
                            <Input
                              type="text"
                              value={
                                formData.deepLinkButton
                                  ?.label?.[locale] || ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  "deepLinkButton",
                                  {
                                    ...formData.deepLinkButton,
                                    label: {
                                      ...formData
                                        .deepLinkButton
                                        ?.label,
                                      [locale]:
                                        e.target.value,
                                    },
                                  }
                                )
                              }
                              placeholder={`Button mətni - ${
                                locale === "az"
                                  ? "Azərbaycan"
                                  : locale === "en"
                                  ? "English"
                                  : "Русский"
                              }`}
                              theme={theme}
                            />
                          </TabPanel>
                        )
                      )}
                    </FormGroup>

                    <FormGroup theme={theme}>
                      <Label theme={theme}>
                        Button Tipi
                      </Label>
                      <Select
                        value={
                          formData.deepLinkButton?.type ||
                          "PRIMARY"
                        }
                        onChange={(e) =>
                          handleInputChange(
                            "deepLinkButton",
                            {
                              ...formData.deepLinkButton,
                              type: e.target.value,
                            }
                          )
                        }
                        theme={theme}
                      >
                        <optgroup label="Full">
                          <option value="STANDARD">
                            Standart
                          </option>
                          <option value="SUCCESS">
                            Success
                          </option>
                          <option value="WARNING">
                            Warning
                          </option>
                          <option value="DANGER">
                            Danger
                          </option>
                          <option value="BLACK">
                            Black
                          </option>
                          <option value="WHITE">
                            White
                          </option>
                          <option value="PRIMARY">
                            Primary
                          </option>
                        </optgroup>
                        <optgroup label="Outlined">
                          <option value="STANDARD_OUTLINED">
                            Standart (Outlined)
                          </option>
                          <option value="SUCCESS_OUTLINED">
                            Success (Outlined)
                          </option>
                          <option value="WARNING_OUTLINED">
                            Warning (Outlined)
                          </option>
                          <option value="DANGER_OUTLINED">
                            Danger (Outlined)
                          </option>
                          <option value="BLACK_OUTLINED">
                            Black (Outlined)
                          </option>
                          <option value="WHITE_OUTLINED">
                            White (Outlined)
                          </option>
                          <option value="PRIMARY_OUTLINED">
                            Primary (Outlined)
                          </option>
                        </optgroup>
                      </Select>
                    </FormGroup>

                    <FormGroup theme={theme}>
                      <Label theme={theme}>
                        Button Icon
                      </Label>
                      <IconPicker
                        value={
                          formData.deepLinkButton?.icon ||
                          ""
                        }
                        onChange={(iconName) =>
                          handleInputChange(
                            "deepLinkButton",
                            {
                              ...formData.deepLinkButton,
                              icon: iconName,
                            }
                          )
                        }
                        theme={theme}
                      />
                      <small
                        style={{
                          color:
                            getTheme(isDark).colors
                              .textSecondary,
                          marginTop:
                            getTheme(isDark).spacing.xs,
                          display: "block",
                        }}
                      >
                        Material-UI icon adı - Icon seçmək
                        üçün axtarış düyməsinə klikləyin
                      </small>
                    </FormGroup>
                  </>
                )}
              </>
            )}
          </StepContentContainer>
        );

      case 1:
        return (
          <StepContentContainer theme={theme}>
            <FormGroup theme={theme}>
              <Label theme={theme}>
                Çatdırılma Kanalları <Required>*</Required>
              </Label>
              <RadioGroup theme={theme}>
                <RadioLabel theme={theme}>
                  <input
                    type="radio"
                    name="channelType"
                    value="PUSH"
                    checked={formData.channelTypes.includes(
                      "PUSH"
                    )}
                    onChange={() =>
                      handleChannelTypeChange("PUSH")
                    }
                  />
                  Push Notification
                </RadioLabel>
                <RadioLabel theme={theme}>
                  <input
                    type="radio"
                    name="channelType"
                    value="ASSISTANT_MESSAGE"
                    checked={formData.channelTypes.includes(
                      "ASSISTANT_MESSAGE"
                    )}
                    onChange={() =>
                      handleChannelTypeChange(
                        "ASSISTANT_MESSAGE"
                      )
                    }
                  />
                  Assistant Message
                </RadioLabel>
              </RadioGroup>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Səs</Label>
              <Select
                value={formData.sound}
                onChange={(e) =>
                  handleInputChange("sound", e.target.value)
                }
                theme={theme}
              >
                <option value="default">Default</option>
                <option value="notification">
                  Notification
                </option>
                <option value="alert">Alert</option>
              </Select>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Prioritet</Label>
              <Select
                value={formData.priority}
                onChange={(e) =>
                  handleInputChange(
                    "priority",
                    e.target.value
                  )
                }
                theme={theme}
              >
                <option value="high">Yüksək</option>
                <option value="normal">Normal</option>
                <option value="low">Aşağı</option>
              </Select>
            </FormGroup>

            <ToggleContainer theme={theme}>
              <ToggleLabel theme={theme}>
                Bildirişlər istifadəçilərdə saxlanılsın
                <small
                  style={{
                    color:
                      getTheme(isDark).colors.textSecondary,
                    fontWeight: "normal",
                    marginLeft: getTheme(isDark).spacing.xs,
                  }}
                >
                  (App-da bildirişlər hissəsində görünəcək)
                </small>
              </ToggleLabel>
              <ToggleSwitch
                theme={theme}
                $checked={formData.saveToUserHistory}
                onClick={() =>
                  handleInputChange(
                    "saveToUserHistory",
                    !formData.saveToUserHistory
                  )
                }
              />
            </ToggleContainer>
          </StepContentContainer>
        );

      case 2:
        return (
          <StepContentContainer theme={theme}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: getTheme(isDark).spacing.lg,
              }}
            >
              <Label theme={theme}>
                Trigger Konfiqurasiyası
              </Label>
              <Button theme={theme} onClick={addTrigger}>
                + Trigger Əlavə Et
              </Button>
            </div>

            {formData.triggers &&
            formData.triggers.length > 0 ? (
              formData.triggers.map((trigger, index) => (
                <TriggerCard key={index} theme={theme}>
                  <TriggerHeader theme={theme}>
                    <TriggerTitle theme={theme}>
                      Trigger #{index + 1}
                    </TriggerTitle>
                    <ActionButton
                      theme={theme}
                      variant="danger"
                      onClick={() => removeTrigger(index)}
                    >
                      Sil
                    </ActionButton>
                  </TriggerHeader>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>
                      Trigger Tipi
                    </Label>
                    <Select
                      value={trigger.type}
                      onChange={(e) =>
                        updateTrigger(
                          index,
                          "type",
                          e.target.value
                        )
                      }
                      theme={theme}
                    >
                      <option value="CUSTOMER_FUNNEL">
                        Customer Funnel
                      </option>
                      <option value="CUSTOMER_ACTION">
                        Customer Action
                      </option>
                      <option value="MANUAL">Manual</option>
                    </Select>
                  </FormGroup>

                  {trigger.type === "CUSTOMER_FUNNEL" && (
                    <>
                      <FormGroup theme={theme}>
                        <Label theme={theme}>
                          Funnel Seç
                        </Label>
                        <FunnelSelect
                          value={trigger.funnelId || ""}
                          onChange={(e) =>
                            updateTrigger(
                              index,
                              "funnelId",
                              e.target.value
                            )
                          }
                          theme={theme}
                        >
                          <option value="">
                            Funnel seçin...
                          </option>
                          {funnels.map((funnel) => (
                            <option
                              key={funnel._id || funnel.id}
                              value={
                                funnel._id || funnel.id
                              }
                            >
                              {funnel.name}
                            </option>
                          ))}
                        </FunnelSelect>
                      </FormGroup>

                      <FormGroup theme={theme}>
                        <Label theme={theme}>
                          Təkrarlanma
                        </Label>
                        <Select
                          value={
                            trigger.isRecurring
                              ? "recurring"
                              : "once"
                          }
                          onChange={(e) =>
                            updateTrigger(
                              index,
                              "isRecurring",
                              e.target.value === "recurring"
                            )
                          }
                          theme={theme}
                        >
                          <option value="once">
                            1 dəfə
                          </option>
                          <option value="recurring">
                            Təkrarlanan
                          </option>
                        </Select>
                        <small
                          style={{
                            color:
                              getTheme(isDark).colors
                                .textSecondary,
                          }}
                        >
                          Bildirişin bir dəfə və ya
                          təkrarlanan göndərilməsi
                        </small>
                      </FormGroup>

                      <FormGroup theme={theme}>
                        <Label theme={theme}>Saat</Label>
                        <Input
                          type="time"
                          value={trigger.scheduleTime || ""}
                          onChange={(e) =>
                            updateTrigger(
                              index,
                              "scheduleTime",
                              e.target.value
                            )
                          }
                          theme={theme}
                        />
                        <small
                          style={{
                            color:
                              getTheme(isDark).colors
                                .textSecondary,
                          }}
                        >
                          Bildirişin göndəriləcəyi saat
                          (HH:mm formatında)
                        </small>
                      </FormGroup>
                    </>
                  )}

                  {trigger.type === "CUSTOMER_ACTION" && (
                    <FormGroup theme={theme}>
                      <Label theme={theme}>
                        Action Tipi
                      </Label>
                      <Select
                        value={trigger.actionType || ""}
                        onChange={(e) =>
                          updateTrigger(
                            index,
                            "actionType",
                            e.target.value
                          )
                        }
                        theme={theme}
                      >
                        <option value="">
                          Action seçin...
                        </option>
                        <option value="LOGIN">Login</option>
                        <option value="PROFILE_COMPLETED">
                          Profile Completed
                        </option>
                        <option value="MEAL_LOG_CREATED">
                          Meal Log Created
                        </option>
                        <option value="WATER_LOG_CREATED">
                          Water Log Created
                        </option>
                        <option value="WEIGHT_LOG_CREATED">
                          Weight Log Created
                        </option>
                        <option value="WALKING_LOG_UPDATED">
                          Walking Log Updated
                        </option>
                        <option value="FASTING_STARTED">
                          Fasting Started
                        </option>
                        <option value="FASTING_STOPPED">
                          Fasting Stopped
                        </option>
                        <option value="MEAL_PLAN_GENERATION_COMPLETED">
                          Meal Plan Generation Completed
                        </option>
                      </Select>
                    </FormGroup>
                  )}

                  <FormGroup theme={theme}>
                    <Label theme={theme}>
                      Gecikmə (dəqiqə)
                    </Label>
                    <Input
                      type="number"
                      value={trigger.delay || 0}
                      onChange={(e) =>
                        updateTrigger(
                          index,
                          "delay",
                          parseInt(e.target.value) || 0
                        )
                      }
                      min="0"
                      theme={theme}
                    />
                    <small
                      style={{
                        color:
                          getTheme(isDark).colors
                            .textSecondary,
                      }}
                    >
                      Bildiriş göndərilməzdən əvvəl gözləmə
                      müddəti
                    </small>
                  </FormGroup>
                </TriggerCard>
              ))
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: getTheme(isDark).spacing.xl,
                  color:
                    getTheme(isDark).colors.textSecondary,
                }}
              >
                <p>Hələ trigger əlavə edilməyib</p>
                <Button
                  theme={theme}
                  onClick={addTrigger}
                  style={{
                    marginTop: getTheme(isDark).spacing.md,
                  }}
                >
                  İlk Trigger-i Əlavə Et
                </Button>
              </div>
            )}
          </StepContentContainer>
        );

      case 3:
        return (
          <StepContentContainer theme={theme}>
            <PreviewCard theme={theme}>
              <PreviewTitle theme={theme}>
                Bildiriş Önizləməsi
              </PreviewTitle>
              <div
                style={{
                  padding: getTheme(isDark).spacing.lg,
                  backgroundColor: formData.backgroundColor,
                  borderRadius:
                    getTheme(isDark).borderRadius.md,
                  marginBottom: getTheme(isDark).spacing.md,
                }}
              >
                {mediaType === "image" && imagePreview && (
                  <ImagePreview
                    src={imagePreview}
                    alt="Preview"
                    theme={theme}
                    style={{
                      marginBottom:
                        getTheme(isDark).spacing.md,
                      maxHeight: "200px",
                    }}
                  />
                )}
                {mediaType === "icon" &&
                  formData.icon &&
                  (() => {
                    const IconComponent =
                      Icons[formData.icon];
                    return IconComponent ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "60px",
                          height: "60px",
                          borderRadius: "50%",
                          backgroundColor:
                            getTheme(isDark).colors
                              .primary + "20",
                          border: `2px solid ${
                            getTheme(isDark).colors.primary
                          }`,
                          margin: "0 auto",
                          marginBottom:
                            getTheme(isDark).spacing.md,
                        }}
                      >
                        <IconComponent
                          style={{
                            fontSize: 32,
                            color:
                              getTheme(isDark).colors
                                .primary,
                          }}
                        />
                      </div>
                    ) : null;
                  })()}
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: formData.titleTextColor,
                    marginBottom:
                      getTheme(isDark).spacing.sm,
                  }}
                >
                  {formData.aiMode
                    ? "(AI tərəfindən yaradılacaq)"
                    : formData.title?.az ||
                      formData.title?.en ||
                      formData.title?.ru ||
                      "Başlıq"}
                </div>
                <PreviewBody
                  theme={theme}
                  style={{ color: formData.bodyTextColor }}
                >
                  {formData.aiMode
                    ? "(AI tərəfindən yaradılacaq)"
                    : formData.body?.az ||
                      formData.body?.en ||
                      formData.body?.ru ||
                      "Məzmun"}
                </PreviewBody>
                {!formData.aiMode &&
                  (formData.title || formData.body) && (
                    <div
                      style={{
                        marginTop:
                          getTheme(isDark).spacing.md,
                        padding:
                          getTheme(isDark).spacing.sm,
                        backgroundColor: `${
                          getTheme(isDark).colors.primary
                        }10`,
                        borderRadius:
                          getTheme(isDark).borderRadius.sm,
                        fontSize: "11px",
                        color:
                          getTheme(isDark).colors
                            .textSecondary,
                      }}
                    >
                      <strong>Önizləmə dili:</strong>{" "}
                      Azərbaycan (az) |
                      {formData.title?.en ||
                      formData.body?.en
                        ? " English (en) mövcuddur"
                        : " English (en) yoxdur"}{" "}
                      |
                      {formData.title?.ru ||
                      formData.body?.ru
                        ? " Русский (ru) mövcuddur"
                        : " Русский (ru) yoxdur"}
                    </div>
                  )}
                {formData.aiMode && (
                  <div
                    style={{
                      marginTop:
                        getTheme(isDark).spacing.md,
                      padding: getTheme(isDark).spacing.sm,
                      backgroundColor: `${
                        getTheme(isDark).colors.primary
                      }20`,
                      borderRadius:
                        getTheme(isDark).borderRadius.sm,
                      fontSize: "12px",
                      color:
                        getTheme(isDark).colors.primary,
                    }}
                  >
                    <strong>AI Mode:</strong> Aktiv |{" "}
                    <strong>Model:</strong>{" "}
                    {formData.aiModel} |{" "}
                    <strong>Prompt:</strong>{" "}
                    {formData.aiPrompt || "Yoxdur"}
                  </div>
                )}
              </div>

              {formData.deepLink &&
                formData.deepLinkButton?.label &&
                (formData.deepLinkButton.label?.az ||
                  formData.deepLinkButton.label?.en ||
                  formData.deepLinkButton.label?.ru) &&
                (() => {
                  const buttonType =
                    formData.deepLinkButton.type ||
                    "PRIMARY";
                  const isOutlined =
                    buttonType.endsWith("_OUTLINED");
                  const baseType = isOutlined
                    ? buttonType.replace("_OUTLINED", "")
                    : buttonType;

                  const buttonColors = {
                    STANDARD: {
                      bg: "#9E9E9E",
                      text: "#FFFFFF",
                      border: "#9E9E9E",
                    },
                    SUCCESS: {
                      bg: "#4CAF50",
                      text: "#FFFFFF",
                      border: "#4CAF50",
                    },
                    WARNING: {
                      bg: "#FF9800",
                      text: "#FFFFFF",
                      border: "#FF9800",
                    },
                    DANGER: {
                      bg: "#F44336",
                      text: "#FFFFFF",
                      border: "#F44336",
                    },
                    BLACK: {
                      bg: "#000000",
                      text: "#FFFFFF",
                      border: "#000000",
                    },
                    WHITE: {
                      bg: "#FFFFFF",
                      text: "#000000",
                      border: "#FFFFFF",
                    },
                    PRIMARY: {
                      bg: getTheme(isDark).colors.primary,
                      text: "#FFFFFF",
                      border:
                        getTheme(isDark).colors.primary,
                    },
                  };

                  const colors =
                    buttonColors[baseType] ||
                    buttonColors.PRIMARY;
                  const IconComponent =
                    formData.deepLinkButton.icon &&
                    Icons[formData.deepLinkButton.icon]
                      ? Icons[formData.deepLinkButton.icon]
                      : null;

                  return (
                    <div
                      style={{
                        marginTop:
                          getTheme(isDark).spacing.md,
                        padding:
                          getTheme(isDark).spacing.md,
                        border: `1px solid ${
                          getTheme(isDark).colors.border
                        }`,
                        borderRadius:
                          getTheme(isDark).borderRadius.md,
                        textAlign: "center",
                      }}
                    >
                      <Button
                        theme={theme}
                        variant={
                          isOutlined ? "outline" : "default"
                        }
                        style={{
                          backgroundColor: isOutlined
                            ? "transparent"
                            : colors.bg,
                          color: isOutlined
                            ? colors.border
                            : colors.text,
                          borderColor: colors.border,
                        }}
                      >
                        {IconComponent && (
                          <span
                            style={{
                              marginRight: "8px",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <IconComponent
                              style={{ fontSize: 18 }}
                            />
                          </span>
                        )}
                        {formData.deepLinkButton.label
                          ?.az ||
                          formData.deepLinkButton.label
                            ?.en ||
                          formData.deepLinkButton.label
                            ?.ru ||
                          ""}
                      </Button>
                    </div>
                  );
                })()}

              <div
                style={{
                  marginTop: getTheme(isDark).spacing.lg,
                }}
              >
                <Label theme={theme}>
                  Konfiqurasiya Məlumatları
                </Label>
                <div
                  style={{
                    padding: getTheme(isDark).spacing.md,
                    backgroundColor:
                      getTheme(isDark).colors.hover,
                    borderRadius:
                      getTheme(isDark).borderRadius.sm,
                    fontSize: "12px",
                    color:
                      getTheme(isDark).colors.textSecondary,
                    marginTop: getTheme(isDark).spacing.sm,
                  }}
                >
                  <div>
                    <strong>Key:</strong> {formData.key}
                  </div>
                  <div>
                    <strong>Tip:</strong> {formData.variant}
                  </div>
                  <div>
                    <strong>AI Mode:</strong>{" "}
                    {formData.aiMode ? "Aktiv" : "Passiv"}
                  </div>
                  {formData.aiMode && (
                    <>
                      <div>
                        <strong>AI Model:</strong>{" "}
                        {formData.aiModel}
                      </div>
                      <div>
                        <strong>AI Prompt:</strong>{" "}
                        {formData.aiPrompt || "Yoxdur"}
                      </div>
                    </>
                  )}
                  <div>
                    <strong>Kanallar:</strong>{" "}
                    {formData.channelTypes.join(", ") ||
                      "Yoxdur"}
                  </div>
                  <div>
                    <strong>
                      İstifadəçilərdə saxlanılsın:
                    </strong>{" "}
                    {formData.saveToUserHistory
                      ? "Bəli"
                      : "Xeyr"}
                  </div>
                  <div>
                    <strong>Trigger-lər:</strong>{" "}
                    {formData.triggers.length || 0}
                  </div>
                  {formData.triggers.length > 0 && (
                    <div
                      style={{
                        marginTop:
                          getTheme(isDark).spacing.xs,
                      }}
                    >
                      {formData.triggers.map(
                        (trigger, idx) => (
                          <div
                            key={idx}
                            style={{
                              marginTop:
                                getTheme(isDark).spacing.xs,
                              paddingLeft:
                                getTheme(isDark).spacing.sm,
                              borderLeft: `2px solid ${
                                getTheme(isDark).colors
                                  .primary
                              }`,
                            }}
                          >
                            <div>
                              <strong>Tip:</strong>{" "}
                              {trigger.type ===
                              "CUSTOMER_FUNNEL"
                                ? "Customer Funnel"
                                : trigger.type ===
                                  "CUSTOMER_ACTION"
                                ? "Customer Action"
                                : "Manual"}
                            </div>
                            {trigger.type ===
                              "CUSTOMER_FUNNEL" && (
                              <>
                                {trigger.isRecurring !==
                                  undefined && (
                                  <div>
                                    <strong>
                                      Təkrarlanma:
                                    </strong>{" "}
                                    {trigger.isRecurring
                                      ? "Təkrarlanan"
                                      : "1 dəfə"}
                                  </div>
                                )}
                                {trigger.scheduleTime && (
                                  <div>
                                    <strong>Saat:</strong>{" "}
                                    {trigger.scheduleTime}
                                  </div>
                                )}
                              </>
                            )}
                            {trigger.delay > 0 && (
                              <div>
                                <strong>Gecikmə:</strong>{" "}
                                {trigger.delay} dəqiqə
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}
                  {formData.deepLink && (
                    <>
                      <div>
                        <strong>Deep Link:</strong>{" "}
                        {getScreenLabel(formData.deepLink)}{" "}
                        (
                        {normalizeDeepLinkForSave(
                          formData.deepLink
                        )}
                        )
                      </div>
                      <div>
                        <strong>Navigasiya Üsulu:</strong>{" "}
                        {formData.actionType ===
                        "DIRECT_LINK"
                          ? "Birbaşa"
                          : "Button ilə"}
                      </div>
                      {formData.actionType === "MODAL" &&
                        formData.deepLinkButton?.label &&
                        (formData.deepLinkButton.label
                          ?.az ||
                          formData.deepLinkButton.label
                            ?.en ||
                          formData.deepLinkButton.label
                            ?.ru) && (
                          <>
                            <div>
                              <strong>Button Label:</strong>{" "}
                              {formData.deepLinkButton.label
                                ?.az ||
                                formData.deepLinkButton
                                  .label?.en ||
                                formData.deepLinkButton
                                  .label?.ru ||
                                ""}
                            </div>
                            <div>
                              <strong>Button Tipi:</strong>{" "}
                              {formData.deepLinkButton.type}
                            </div>
                            {formData.deepLinkButton
                              .icon && (
                              <div>
                                <strong>
                                  Button Icon:
                                </strong>{" "}
                                {
                                  formData.deepLinkButton
                                    .icon
                                }
                              </div>
                            )}
                          </>
                        )}
                    </>
                  )}
                </div>
              </div>
            </PreviewCard>
          </StepContentContainer>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingContainer theme={theme}>
          Yüklənir...
        </LoadingContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <Header>
          <BackButton
            onClick={() => navigate("/admin/notifications")}
            theme={theme}
            variant="outline"
          >
            <ArrowBack /> Geri
          </BackButton>
          <Title theme={theme}>
            {isEditMode
              ? "Bildirişi Redaktə Et"
              : "Yeni Bildiriş Yarat"}
          </Title>
        </Header>

        <FormCard theme={theme}>
          {error && (
            <ErrorMessage theme={theme}>
              {error}
            </ErrorMessage>
          )}

          <StyledStepper
            activeStep={activeStep}
            orientation="horizontal"
            theme={theme}
          >
            {steps.map((step, index) => (
              <Step
                key={step.label}
                completed={index < activeStep}
              >
                <StepLabel icon={step.icon}>
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </StyledStepper>

          {renderStepContent()}

          <NavigationButtons theme={theme}>
            <Button
              theme={theme}
              variant="outline"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              <ArrowBack /> Geri
            </Button>
            <div
              style={{
                display: "flex",
                gap: getTheme(isDark).spacing.sm,
              }}
            >
              {activeStep < steps.length - 1 ? (
                <Button theme={theme} onClick={handleNext}>
                  Növbəti <ArrowForward />
                </Button>
              ) : (
                <Button
                  theme={theme}
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    "Saxlanılır..."
                  ) : (
                    <>
                      <Save /> Yadda Saxla
                    </>
                  )}
                </Button>
              )}
            </div>
          </NavigationButtons>
        </FormCard>
      </PageContainer>
    </MainLayout>
  );
};

export default NotificationForm;
